import { TipTapRender } from "@troop.com/tiptap-react-render";
import { Box, Link, Typography } from "components/ui";
import React from "react";
// import ReactPlayer from "react-player"; // Remove ReactPlayer import
import { Image as ImageNative } from "react-native";
import { useIsMobile } from "utils/hooks/useIsMobile";
import { PageVideo } from "./PageVideo"; // Import local PageVideo component
// import { RichTextEditorContainer } from "../PageBuilder.style";

type Props = {
	content?: any;
	contentJSON?: any;
};

type NodeHandler = (props: any) => JSX.Element; // Revert NodeHandler type

export const PageRichTextEditor = (props: Props) => {
	const { content, contentJSON } = props;
	const isMobile = useIsMobile();

	// if (isMobile) return null;

	// handle the document
	const doc: NodeHandler = (props) => <Box sx={{ width: "100%" }}>{props.children}</Box>;

	// handle a paragraph with proper spacing
	const paragraph: NodeHandler = (props) => {
		return (
			<Typography.Paragraph
				sx={{
					fontSize: 16,
					lineHeight: "1.5",
					mb: 24,
					textAlign: props.node.attrs?.textAlign || "left",
					// color: "#333",
				}}
			>
				{props.children}
			</Typography.Paragraph>
		);
	};

	// handle text with proper rendering
	const text: NodeHandler = (props) => {
		// process text marks
		if (props.node.marks && props.node.marks.length > 0) {
			return props.node.marks.reduce(
				(node: React.ReactNode, mark: any) => {
					switch (mark.type) {
						case "bold":
							return <strong style={{ fontWeight: 700 }}>{node}</strong>;
						case "italic":
							return <em>{node}</em>;
						case "underline":
							return <u>{node}</u>;
						case "strike":
							return <s>{node}</s>;
						case "link":
							return (
								<Link href={mark.attrs.href}>
									<Typography style={{ color: "#0066cc", textDecorationLine: "underline" }}>{node}</Typography>
								</Link>
								// <a href={mark.attrs.href} target="_blank" rel="noopener noreferrer" style={{ color: "#0066cc", textDecoration: "underline" }}>
								// 	{node}
								// </a>
							);
						default:
							return node;
					}
				},
				<span>{props.node.text}</span>,
			);
		}
		return <span>{props.node.text}</span>;
	};

	// handle an image with full width layout
	const img: NodeHandler = (props) => {
		const { src, alt, title } = props.node.attrs;
		return (
			<Box my={24} style={{ alignItems: "center" }}>
				<ImageNative
					source={typeof src === "string" ? { uri: src } : src}
					style={{
						width: "100%", // or a fixed number: 300
						aspectRatio: 1.78,
					}}
					resizeMode="contain" // preserve aspect ratio
				/>
				{/* {alt ? (
					<Typography
						sx={{
							fontSize: 14,
							mt: 1,
							color: "#666",
							textAlign: "center",
						}}
					>
						{alt}
					</Typography>
				) : null} */}
			</Box>
		);
	};

	// handle video embeds
	const video: NodeHandler = (props) => {
		const { src } = props.node.attrs;

		// Use the local PageVideo component
		// Add a wrapper Box for spacing like other elements
		return (
			<Box my={24}>
				<PageVideo videoUrl={src || ""} />
			</Box>
		);

		// if (!src) {
		// 	return null; // Don't render if no src is provided
		// }

		// return (
		// 	<Box
		// 		my={24} // Vertical margin for spacing
		// 		position="relative"
		// 		paddingBottom="56.25%" // 16:9 aspect ratio
		// 		height={0}
		// 		overflow="hidden"
		// 		maxWidth="100%"
		// 		bg="$neutral25" // Background color while loading/if error
		// 	>
		// 		<ReactPlayer
		// 			url={src}
		// 			controls // Show player controls
		// 			width="100%"
		// 			height="100%"
		// 			style={{ position: 'absolute', top: 0, left: 0 }}
		// 		/>
		// 	</Box>
		// );
	};

	// handle headings with proper spacing
	const heading: NodeHandler = (props) => {
		const level = props.node.attrs.level || 1;
		// Define heading sizes with better hierarchy
		const fontSize = level === 1 ? 28 : level === 2 ? 24 : level === 3 ? 20 : 18;
		const marginTop = level === 1 ? 6 : 4;
		const marginBottom = level === 1 ? 4 : 3;

		return (
			<Typography
				weight="bold"
				sx={{
					fontSize,
					fontWeight: 700,
					lineHeight: "1.3",
					marginTop: marginTop,
					marginBottom: marginBottom,
					color: "#111",
					mb: 12,
					textAlign: props.node.attrs?.textAlign || "left",
				}}
			>
				{props.children}
			</Typography>
		);
	};

	// handle bullet list with better spacing
	const bulletList: NodeHandler = (props) => {
		return (
			<ul
				style={{
					listStyleType: "disc",
					paddingLeft: 24,
					marginTop: 16,
					marginBottom: 24,
				}}
			>
				{props.children}
			</ul>
		);
	};

	// handle ordered list with better spacing
	const orderedList: NodeHandler = (props) => {
		return (
			<ol
				style={{
					listStyleType: "decimal",
					paddingLeft: 24,
					marginTop: 16,
					marginBottom: 24,
					fontFamily: "sans-serif", // Ensure consistent font for numbering
				}}
			>
				{props.children}
			</ol>
		);
	};

	// handle list item with proper spacing
	const listItem: NodeHandler = (props) => {
		return <li style={{ marginBottom: 12, lineHeight: 1.5 }}>{props.children}</li>;
	};

	// handle blockquote with better styling
	const blockquote: NodeHandler = (props) => {
		return (
			<blockquote
				style={{
					borderLeft: "4px solid #ccc",
					paddingLeft: 16,
					paddingRight: 8,
					paddingTop: 4,
					paddingBottom: 4,
					fontStyle: "italic",
					margin: "24px 0",
					backgroundColor: "rgba(0,0,0,0.03)",
					borderRadius: "0 4px 4px 0",
				}}
			>
				{props.children}
			</blockquote>
		);
	};

	// handle horizontal rule with better styling
	const horizontalRule: NodeHandler = () => {
		return (
			<hr
				style={{
					margin: "24px 0",
					border: "none",
					height: "1px",
					backgroundColor: "#e0e0e0",
				}}
			/>
		);
	};

	// handle hard break (line break)
	const hardBreak: NodeHandler = () => {
		return <br />;
	};

	// handle code block with better styling
	const codeBlock: NodeHandler = (props) => {
		return (
			<pre
				style={{
					backgroundColor: "#f5f5f5",
					padding: 16,
					borderRadius: 8,
					overflowX: "auto",
					fontFamily: "monospace",
					fontSize: 14,
					lineHeight: 1.6,
					marginBottom: 24,
					boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
				}}
			>
				<code>{props.children}</code>
			</pre>
		);
	};

	// handle table
	const table: NodeHandler = (props) => {
		return (
			<div style={{ overflowX: "auto", marginBottom: 24 }}>
				<table
					style={{
						borderCollapse: "collapse",
						width: "100%",
						fontSize: 14,
					}}
				>
					{props.children}
				</table>
			</div>
		);
	};

	const tableRow: NodeHandler = (props) => {
		return <tr>{props.children}</tr>;
	};

	const tableHeader: NodeHandler = (props) => {
		return (
			<th
				style={{
					backgroundColor: "#f5f5f5",
					fontWeight: 600,
					padding: 12,
					borderBottom: "2px solid #ddd",
					textAlign: "left",
				}}
			>
				{props.children}
			</th>
		);
	};

	const tableCell: NodeHandler = (props) => {
		return (
			<td
				style={{
					padding: 12,
					borderBottom: "1px solid #ddd",
				}}
			>
				{props.children}
			</td>
		);
	};

	// create a handlers wrapper
	const handlers: any = {
		doc,
		text,
		paragraph,
		image: img,
		heading,
		bulletList,
		orderedList,
		listItem,
		blockquote,
		horizontalRule,
		hardBreak,
		codeBlock,
		youtube: video,
		table,
		tableRow,
		tableHeader,
		tableCell,
	};

	return (
		<Box
			sx={{
				width: "100%",
				maxWidth: "100%", // Allow content to use full width
				mx: "auto",
				px: 3,
			}}
		>
			<TipTapRender handlers={handlers} node={contentJSON || content} />
		</Box>
	);
};

export const PageRichTextEditorProps = {
	content: "Start typing...",
	contentJSON: null,
};

PageRichTextEditor.craft = {
	props: PageRichTextEditorProps,
	related: {
		settings: [
			{
				name: "Content",
				props: ["contentJSON", "content"],
			},
		],
	},
};
